/**
 * 事件追踪 Store
 * 管理安装状态、上线时间等事件追踪数据
 */

import { ref } from 'vue'
import type { IStorage } from '../storage/Storage'
import { STORAGE_KEYS } from '../../constants/storage-keys'
import { BaseStore } from './BaseStore'

export interface EventTrackingState {
  installStatus: number
  onlineTime: string | null
}

export class EventTrackingStore extends BaseStore<EventTrackingState> {
  private _installStatus = ref<number>(0)
  private _onlineTime = ref<string | null>(null)

  constructor(storage: IStorage) {
    super(storage, STORAGE_KEYS.INSTALL_STATUS)
    this.hydrate()
  }

  // Getters
  get installStatus(): number {
    return this._installStatus.value
  }

  get onlineTime(): string | null {
    return this._onlineTime.value
  }

  // Setters
  set installStatus(value: number) {
    this._installStatus.value = value
    this.persist()
  }

  set onlineTime(value: string | null) {
    this._onlineTime.value = value
    this.persist()
  }

  /**
   * 标记已安装
   */
  markInstalled(): void {
    this._installStatus.value = 1
    this.persist()
  }

  /**
   * 标记已上线
   */
  markOnline(): void {
    this._onlineTime.value = new Date().toISOString().split('T')[0]
    this.persist()
  }

  /**
   * 检查是否需要上报安装事件
   */
  shouldReportInstall(): boolean {
    return this._installStatus.value !== 1
  }

  /**
   * 检查是否需要上报上线事件
   */
  shouldReportOnline(): boolean {
    if (!this._onlineTime.value) {
      return true
    }
    const today = new Date().toISOString().split('T')[0]
    return this._onlineTime.value !== today
  }

  /**
   * 序列化状态
   */
  serialize(): string {
    return JSON.stringify({
      installStatus: this._installStatus.value,
      onlineTime: this._onlineTime.value
    })
  }

  /**
   * 反序列化状态
   */
  deserialize(data: string): void {
    try {
      const state = JSON.parse(data) as EventTrackingState
      this._installStatus.value = state.installStatus || 0
      this._onlineTime.value = state.onlineTime || null
    } catch (error) {
      console.error('[EventTrackingStore] Failed to deserialize state:', error)
    }
  }

  /**
   * 获取当前状态
   */
  getState(): EventTrackingState {
    return {
      installStatus: this._installStatus.value,
      onlineTime: this._onlineTime.value
    }
  }

  /**
   * 清除所有数据
   */
  clear(): void {
    this._installStatus.value = 0
    this._onlineTime.value = null
    super.clear()
  }
}
